import React from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MessageIcon from "@mui/icons-material/Message";
import moment from "moment";

import Typography from "components/basic/typography";

import { initials } from "../../helpers/initials";
import { Booking } from "../../../../types";

interface CreatedByProps {
  booking: Booking;
  isCustomer: boolean;
  hasMyQuotes: boolean;
  onViewQuotes: () => void;
  onMessage?: () => void;
}

const CreatedBy: React.FC<CreatedByProps> = ({
  booking,
  isCustomer,
  hasMyQuotes,
  onViewQuotes,
  onMessage,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");

  if (!booking.createdByName) {
    return null;
  }

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="caption"
        style={{
          color: theme.palette.text.secondary,
          display: "block",
          marginBottom: 8,
        }}
      >
        {isCustomer ? t("details.organized_by") : t("details.created_by")}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
          {initials(booking.createdByName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2">{booking.createdByName}</Typography>
          {booking.createdAt && (
            <Typography
              variant="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              {moment(Number(booking.createdAt)).format("MMM D, YYYY · h:mm A")}
            </Typography>
          )}
        </Box>
        {hasMyQuotes && (
          <IconButton
            size="small"
            title={t("details.view_quotes")}
            onClick={onViewQuotes}
            sx={{ color: theme.palette.info.main }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        {onMessage && (
          <IconButton
            size="small"
            title={t("details.message")}
            onClick={onMessage}
            sx={{ color: theme.palette.warning.main }}
          >
            <MessageIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </>
  );
};

export default CreatedBy;
